import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment'; 
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Button, FormControl, FormControlLabel, FormGroup, Grid, 
         Paper, MenuItem, Select, Switch,
         TextField, Typography
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import AppDeleteMutation from './AppDeleteMutation';
import AppUpdateMutation from './AppUpdateMutation';
import AppDisplay from '../ui/AppDisplay';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import PageTitle from '../ui/PageTitle';
import SaveButton from '../ui/SaveButton';
import ServerError from '../ui/ServerError';
import LoadingSpinner from '../ui/LoadingSpinner';
import SubscriptionsAcrossCustomer from './SubscriptionsAcrossCustomer';
import CurrencyTextField from '../ui/CurrencyTextField';

import AppBindStyles from '../../AppBindStyles';

const abEnvironment = new AbEnvironment();
const ServiceEditQuery = graphql`
query ServiceEditQuery($appId: UUID!) {
  appTByAppId(appId: $appId) {
    appId
    accountId
    appStatus
    parentAppId
    oemAppId
    appName
    appSku
    appUrl
    signupUrl,
    appLogo
    description
    appType
    atId
    appSkuPrice
    appMarkupPercentage
    appMarkupAbsolute
    jitFloatDefault
    marqetaMid
    marqetaMname
    maxCharge
    paymentOrder
    passThroughFees
    wholesaleDiscountPercentage
    billingFrequency
    customerAppTsByAppId {
      totalCount
      nodes {
        customerAppId
        paymentOrder
        appMarkupPercentage
        notes
        appStatus
        wholesaleDiscountPercentage
        accountTByCustomerAccountId {
          accountId
          accountName
        }
        bindCardTByBcId {
          bcStatus
        }
      }
    }
    cpAppTsByAppId {
      totalCount
      nodes {
        cpId
        cpTByCpId {
          programName
          description
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
    },
    flex: {
      flex: 1,
    },
    paper: {
      padding: theme.spacing(3),
      textAlign: 'left',
      color: theme.palette.text.secondary,
      maxWidth: 980,
      marginTop: 16
    },
    paperInner: {
      padding: theme.spacing(2)
    },
    rtable: {
      marginTop: 16,
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 32,
    },
    tblHeadCellLeft: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    [theme.breakpoints.up('sm')]: {
      tblCellTypeCol: {
        paddingLeft: 4,
        paddingRight: 4,
        minWidth: 20,
        width: 20,
      },
      tblCellDescCol: {
        paddingLeft: 10,
        paddingRight: 10,
        minWidth: 140,
        width: 260,
      },
      tblCellNameCol: {
        textAlign: 'left',
        minWidth: 120,
        width: 120,
      },
    },
    [theme.breakpoints.up('xs')]: {
      tblCellTypeCol: {
  
      },
      tblCellDescCol: {
  
      },
      tblCellNameCol: {
        textAlign: 'left'
      },
    },
    currencyField: {
      width: 120,
      marginTop: 4,
      marginBottom: 4
    },
    frequency: {
      width: 120,
      marginTop: 4,
      marginBottom: 4
    }
  });
  
  class ServiceEdit extends Component {
    state = {
        showSpinner: false,
        saveStatus: null,
        saveMessage: null,
        saveSnackbarOpen: false,
        saveSnackbarDuration: null,
        confirmDelete: false, 
        appStatus: null,
        appName: null,
        appSku: null,
        appUrl: null,
        signupUrl: null,
        appLogo: null,
        description: null,
        appType: null,
        appSkuPrice: null,
        appMarkupAbsolute: null,
        appMarkupPercentage: null,
        paymentOrder: null,
        passThroughFees: null,
        wholesaleDiscountPercentage: null,
        //vendorOemTemplateAppCount: 1,
        billingFrequency: null
    }
  
    goTo(route) {
      this.props.history.push(`/${route}`);
    }

    goBack() {
      this.props.history.goBack();
    }
    
    getValueForRender(state_member, prop_member, theDefault)
    {
      if (state_member != null)
        return state_member;
      
      if (prop_member != null)
        return prop_member;
  
      return theDefault;
    }
        
    disableAppSave(appProps) {
      if (appProps === null)
        return true;
  
      let retval =  this.disableAttributeCheck(this.state.appName,             appProps.appName) &&
                    this.disableAttributeCheck(this.state.description,         appProps.description) &&
                    this.disableAttributeCheck(this.state.appSkuPrice,         appProps.appSkuPrice) &&
                    this.disableAttributeCheck(this.state.appStatus,           appProps.appStatus) &&
                    this.disableAttributeCheck(this.state.billingFrequency,    appProps.billingFrequency);

      if (this.areFieldsInError()) {
        return true;
      }            
      
      return retval;
    }
    
    disableAttributeCheck(stateVar, propsVar)
    {
        if (stateVar !== null) {
            if (propsVar === null && stateVar === '') {
                return true;
            }
            if (propsVar !== stateVar) {
                return false;
            }
        }
        return true;
    }
  
    showSpinner() {
      return this.state.showSpinner;
    }

    handleAppStatusChange = (event) => {
      this.setState({'appStatus': parseInt(event.target.value, 10)});
    };
  

    getValueForBillingFrequencyRender(propBillingFrequency) {
      if (this.state.billingFrequency != null) {
        return this.state.billingFrequency;
      }
  
      if (propBillingFrequency != null) {
        return propBillingFrequency;
      }

      return 0;
    }

    getErrorMessage(field, minValue, maxValue) {
      let errMesg = null;
  
      let value = Number(field); 
      if (value < minValue) {
        return 'Cannot be less than ' + minValue;
      }
      if (value > maxValue) {
        return 'Cannot be greater than ' + maxValue;
      }
  
      return errMesg;
    }
  
    areFieldsInError() {
      const { getMaxServiceAmount } = this.props.auth;
      
      if (this.state.appSkuPrice !== null && this.getErrorMessage(this.state.appSkuPrice, 1, getMaxServiceAmount()) !== null) {
        return true;
      }
      return false;
    }
  
    render() {
        const { isAuthenticated, isAuthenticatedReseller, needAssent, logError, getMaxServiceAmount } = this.props.auth;
        const classes = this.props.classes;
  
        if (!isAuthenticated() || needAssent()) {
          return (
            <Redirect to='/home' />
          );
        } else if (isAuthenticatedReseller()) {
          const params = new URLSearchParams(this.props.location.search);
          let appId = params.get('appId');
          let fp = params.get('fp');
    
          let backTo = "Service Catalog";
          if (fp != null && fp !== '') {
            backTo = fp;
          }

          let myAccountId = abEnvironment.getUserAttribute("a_id");

          return (
            <QueryRenderer
              environment={abEnvironment.getEnvironment()}
              query={ServiceEditQuery}
              variables={{appId: appId}}
              render={({error, props})=> {
                if (error) {
                  logError(error.message);
                  return (
                    <ServerError/>
                  );
                } else if (props) {
                  if (props.appTByAppId === null)
                  {
                    return (
                      <PageTitle title="Service not found" showBackButton={true} name={backTo} history={this.props.history}/>
                    );
                  }

                  let theAppName     = this.getValueForRender(this.state.appName,  props.appTByAppId.appName, '');
                  let theAppSku      = this.getValueForRender(this.state.appSku,   props.appTByAppId.appSku,  '');
                  let theDescription = this.getValueForRender(this.state.description, props.appTByAppId.description, '');

  
                  //SDW left this in but table is removed...
                  // Put the subscribers in alpha order 
                  let subscribers = [];
                  if (props.appTByAppId != null && 
                      props.appTByAppId.customerAppTsByAppId != null &&
                      props.appTByAppId.customerAppTsByAppId.nodes != null)
                  {
                    let subsNodes = props.appTByAppId.customerAppTsByAppId.nodes;
                    for (let i=0; i<subsNodes.length; i++) {
                      let n = subsNodes[i];
                      let theAccountName = n.accountTByCustomerAccountId.accountName;
                      let theAccountId   = n.accountTByCustomerAccountId.accountId;
                      let theModel = 'Expense to me';
                      if (n.paymentOrder === 0 || n.paymentOrder === 1) {
                        let mkds = '';
                        if (n.appMarkupPercentage > 0) {
                          mkds = 'with a ' + parseFloat(n.appMarkupPercentage).toFixed(2) + '% markup'
                        } else if (n.appMarkupPercentage < 0) {
                          mkds = 'with a ' + parseFloat(n.appMarkupPercentage * -1).toFixed(2) + '% discount'
                        }
    
                        theModel = 'Expense to ' + theAccountName + ' ' + mkds;
                      }

                      let theHref = '';
                      if (myAccountId !== theAccountId) {
                        theHref = "customer?accountId=" + theAccountId + "&fp=App";
                      }
                      let subsHref = "subscription?customerAppId=" + n.customerAppId + "&fp=App";
    
                      let aSub = {
                        customerAppId: n.customerAppId,
                        accountName: theAccountName,
                        accountHref: theHref,
                        subscriptionHref: subsHref,
                        model: theModel,
                        notes: n.notes,
                        customerAppStatus: n.appStatus,
                        bcStatus: n.bindCardTByBcId.bcStatus,
                      };
                      subscribers.push(aSub);
                    }
                  }
                  // Sort
                  if (subscribers.length > 1) {
                    subscribers.sort((a, b) => {
                      if (a.accountName > b.accountName) { return 1; }
                      if (a.accountName < b.accountName) { return -1; }
                      return 0;
                    });
                  }

                  return (
                    <Grid container spacing={3} direction="row" justifyContent="flex-start">
                      <Grid item xs={12}>
                        <PageTitle title="Manage service" showBackButton={true} name={backTo} noMargin={true} history={this.props.history}/>
                      </Grid>
                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>

                            <Grid item xs={12} >
                              <Grid container direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Grid item xs={12} >
                                  <Paper className={classes.paperInner}>

                                    <Grid container spacing={0} direction="row" justifyContent='space-between' alignItems='flex-start'>
                                      <Grid item xs={10}>
                                        <AppDisplay appLogo={props.appTByAppId.appLogo}
                                                    appName={theAppName}
                                                    appSku={theAppSku}
                                                    description={theDescription}/>
                                      </Grid>
                                      <Grid item>
                                        <FormGroup>
                                          <FormControlLabel
                                            control={
                                              <Switch
                                                checked={this.getValueForRender(this.state.appStatus, 
                                                                                props.appTByAppId.appStatus,
                                                                                '') === 0}
                                                onChange={(e, checked) => {
                                                  if (checked) {
                                                    this.setState({ appStatus: 0});
                                                  } else {
                                                    this.setState({ appStatus: 1});
                                                  }
                                                }}
                                              />
                                            }
                                            label="Active"
                                          />
                                        </FormGroup>
                                      </Grid>
                                    </Grid>
                                  </Paper>
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12} >
                              <Paper className={classes.paperInner}>

                                <Grid container spacing={2} direction="row" justifyContent="space-between" >
                                  <Grid item xs={12}>
                                    <Typography variant="h6">Service description</Typography>
                                  </Grid>                          
        
                                  <Grid item xs={12} md={6}>
                                    <TextField
                                      id="app-name" label="App name" 
                                      value={ theAppName }
                                      onChange={ (e) => this.setState({ appName: e.target.value }) }
                                      inputProps={{ maxLength: 64 }}
                                    />
                                  </Grid>

                                  <Grid item xs={12}>
                                    <TextField
                                      id="description" label="Description" 
                                      value={ theDescription }
                                      multiline
                                      onChange={ (e) => this.setState({ description: e.target.value }) }
                                      inputProps={{ maxLength: 2048 }}
                                    />
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                            <Grid item xs={12}>
                              <Paper className={classes.paperInner}>
                                <Grid container spacing={1} direction="row" justifyContent="space-between" >
                                  <Grid item xs={12}>
                                    <Grid container spacing={0} direction="row" justifyContent="flex-start">
                                      <Grid item xs={12}>
                                        <Typography  variant="h6" style={AppBindStyles.sectionTitle}>Manage terms</Typography>
                                      </Grid>                          
                                      <Grid item xs={12}>
                                        <Typography variant="body2">Manage the default billing terms for {theAppName}</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12}/>
                                  <Grid item xs={12}>
                                    <Grid container justifyContent="space-between" alignItems="center" spacing={0}>
                                      <Grid item>
                                        <Grid container direction="column" spacing={0}>
                                          <Grid item>
                                            <Typography variant="body1" style={AppBindStyles.boldLabel}>{"Amount"}</Typography>
                                          </Grid>
                                          <Grid item style={AppBindStyles.markupDescription}> 
                                            <Typography variant="caption">{'This amount will be billed '}</Typography>
                                          </Grid>

                                        </Grid>
                                      </Grid>
                                      <Grid item > 
                                        <CurrencyTextField id="appSkuPrice" className={classes.currencyField}
                                          value={this.getValueForRender(this.state.appSkuPrice, props.appTByAppId.appSkuPrice, 0)}
                                          showErrMsg={true}
                                          allowNegative={false}
                                          minValue={1}
                                          maxValue={getMaxServiceAmount()}
                                          minEnterValue={0}
                                          maxEnterValue={99999999}
                                          onChange={(e) => this.setState({ appSkuPrice: (e.target.value) ? e.target.value : '0' })}
                                          textAlign="right"
                                        />
                                      </Grid>
                                    </Grid>
                                  </Grid>

                                  <Grid item xs={12}/>
                                  <Grid item>
                                    <Grid container direction="column" spacing={0}>
                                      <Grid item>
                                        <Typography variant="body1"style={AppBindStyles.boldLabel}>{"Frequency"}</Typography>
                                      </Grid>
                                      <Grid item style={AppBindStyles.markupDescription}> 
                                        <Typography variant="caption" >Monthly occurs on customer's billing day of month</Typography>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item> 
                                    <FormControl>
                                      <Select
                                        value={this.getValueForBillingFrequencyRender(props.appTByAppId.billingFrequency) }
                                        className={classes.frequency}
                                        onChange={(e) => { 
                                              this.setState({ billingFrequency: e.target.value }); 
                                        } }
                                        inputProps={{
                                          name: 'billingFrequency',
                                          id: 'billingFrequency',
                                        }}
                                      >                                   
                                        <MenuItem value={0}>One-time fee</MenuItem>
                                        <MenuItem value={1}>Monthly</MenuItem>
                                        {/*<MenuItem value={2}>Annual</MenuItem>*/}
                                      </Select>
                                    </FormControl>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>
                            <Grid item xs={12}>

                              <Grid container spacing={1} direction="row" justifyContent="space-between" >
                                <Grid item xs={12}/>
                                <Grid item >
                                  <SaveButton
                                    snackbarOpen={this.state.saveSnackbarOpen}
                                    autoHideDuration={this.state.saveSnackbarDuration}
                                    status={this.state.saveStatus} 
                                    message={this.state.saveMessage}
                                    disabled={this.disableAppSave(props.appTByAppId)}
                                    onSaveClick={() => {this._updateApp(appId)}}
                                    onSnackbarClose={() => { this._onSnackbarClose(); }}
                                  />
                                  { !this.showSpinner() &&  ((props.appTByAppId.customerAppTsByAppId.totalCount === 0 && isAuthenticatedReseller())) &&
                                    <Button variant="outlined" color="primary"
                                      style={AppBindStyles.bottomButton} onClick={() => { this.setState({confirmDelete:true}); }}>
                                      <Delete color="primary"/> Delete
                                    </Button>
                                  }
                                  <ConfirmationDialog
                                      open={this.state.confirmDelete}
                                      onClose={() => { this.setState({confirmDelete:false});}}
                                      onConfirm={() => { this._deleteApp(appId); }}
                                      onCancel={() => { }}
                                      title="Delete service?"
                                      cancelBtn="Cancel"
                                      okBtn="Ok"
                                    >
                                      If you proceed, the service will be deleted from the catalog.
                                  </ConfirmationDialog>  
                                </Grid>     
                              </Grid>
                            </Grid>
                          </Grid>
                        </Paper>
                      </Grid>
                      
                      {false && isAuthenticatedReseller() && 
                      <Grid item xs={12}>
                        <Paper className={classes.paper}>
                          <Grid container>
                            <Grid item xs={12}>
                              <Typography variant="h6" style={AppBindStyles.sectionTitle}>Subscriptions</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <SubscriptionsAcrossCustomer auth={this.props.auth} history={this.props.history} appId={appId} loginLink={null} fromAppEdit={false}></SubscriptionsAcrossCustomer>
                            </Grid>
                          </Grid>                        
                        </Paper>
                      </Grid>}
                    </Grid>
                  );
                }
                return <LoadingSpinner />
              }}
            />
          )
        } else {
          return (
            <Redirect to='/home' />
          );
        }
    }

    _onSnackbarClose() {
      this.setState( {saveSnackbarOpen: false, saveStatus: '', saveMessage: '', saveSnackbarDuration: null });
    }
    
    _updateApp = (appId) => {
      this.setState({ saveStatus: 'saving' });

      const { appStatus, appName, appSku, appUrl, signupUrl, appLogo, 
              description, appType, appSkuPrice, 
              appMarkupPercentage, appMarkupAbsolute, paymentOrder, passThroughFees, wholesaleDiscountPercentage, billingFrequency } = this.state;
              
      AppUpdateMutation.commit(appId, appStatus, appName, appSku, appUrl, signupUrl, appLogo,
                               description, appType, appSkuPrice,
                              appMarkupAbsolute, appMarkupPercentage, paymentOrder, passThroughFees, wholesaleDiscountPercentage, billingFrequency)
      .then(response => {
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'success', saveMessage: 'Service saved.', saveSnackbarDuration: 3000 });
      })
      .catch(error => {
        this.setState({ showSpinner: false, saveSnackbarOpen: true, saveStatus: 'error', saveMessage: 'Unable to save service', saveSnackbarDuration: null });
      });                      
    }

    _deleteApp = (appId) => {
      this.setState({ showSpinner: true });
      AppDeleteMutation.commit(appId)
      .then(response => {
        this.setState({ showSpinner: false });
        this.goBack();
      })
      .catch(error => {
        this.setState({ showSpinner: false });
        this.goBack();
      });                      
    }
}

ServiceEdit.propTypes = {
    classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(ServiceEdit);
